import { createSlice, createSelector } from '@reduxjs/toolkit';
import { SessionStateType } from '@business/entities/session';
import type { RootState } from './store';

const initialState: SessionStateType = {
  isAdmin: false,
  userPermissions: [],
  notificationType: false,
  subscriberId: null,
  subscriptionPlanName: null,
  signUpCode: null,
  page: null,
  notificationData: null,
  isMediaUploadModalOpen: false,
  moduleForUpload: null,
  isFromRecipesPage: false,
  isFromFavoritePage: false,
  additionalPhotoMeta: {},
  firebaseToken: null,
  memoryFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  photoFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  recipeFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  folderFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false,
    folderId: ''
  },
  momentFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  peopleFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  },
  trashFilterOptions: {
    search: '',
    accessType: 'public',
    sortBy: '',
    sharedWith: '',
    people: '',
    things: '',
    locations: '',
    date: '',
    interactions: '',
    mediaType: '',
    favorites: false
  }
};

export const sessionStateSlice = createSlice({
  name: 'sessionStateSlice',
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
    },
    resetSessionState: () => {
      return initialState;
    },
    setSubscriberId: (state, action) => {
      state.subscriberId = action.payload.subscriberId;
    },
    setSubscriptionPlanName: (state, action) => {
      state.subscriptionPlanName = action.payload;
    },
    setSignUpCode: (state, action) => {
      state.signUpCode = action.payload;
    },
    setIsMediaUploadModalOpen: (state, action) => {
      state.isMediaUploadModalOpen = action.payload;
    },
    setModuleForUpload: (state, action) => {
      state.moduleForUpload = action.payload;
    },
    logoutUser: state => {
      state.isAdmin = false;
      state.token = null;
      state.refreshToken = null;
      state.userPermissions = [];
      state.isMediaUploadModalOpen = false;
      state.subscriberId = null;
      state.additionalPhotoMeta = {};
      state.firebaseToken = null;
      state.subscriptionPlanName = null;
      state.signUpCode = null;
      state.memoryFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.photoFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.recipeFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.folderFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false,
        folderId: ''
      };
      state.momentFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.peopleFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
      state.trashFilterOptions = {
        search: '',
        accessType: 'public',
        sortBy: '',
        sharedWith: '',
        people: '',
        things: '',
        locations: '',
        date: '',
        interactions: '',
        mediaType: '',
        favorites: false
      };
    },
    setUserNotificationData: (state, action) => {
      state.page = action.payload.page;
      state.notificationData = action.payload.notificationData;
    },
    setIsFromRecipesPage: (state, action) => {
      state.isFromRecipesPage = action.payload;
    },
    setIsFromFavoritePage: (state, action) => {
      state.isFromFavoritePage = action.payload;
    },
    setAdditionalPhotoMeta: (state, action) => {
      state.additionalPhotoMeta = action.payload;
    },
    setFirebaseToken: (state, action) => {
      state.firebaseToken = action.payload;
    },
    setMemoryFilterOptions: (state, action) => {
      state.memoryFilterOptions = action.payload;
    },
    setPhotoFilterOptions: (state, action) => {
      state.photoFilterOptions = action.payload;
    },
    setRecipeFilterOptions: (state, action) => {
      state.recipeFilterOptions = action.payload;
    },
    setFolderFilterOptions: (state, action) => {
      state.folderFilterOptions = action.payload;
    },
    setMomentFilterOptions: (state, action) => {
      state.momentFilterOptions = action.payload;
    },
    setPeopleFilterOptions: (state, action) => {
      state.peopleFilterOptions = action.payload;
    },
    setTrashFilterOptions: (state, action) => {
      state.trashFilterOptions = action.payload;
    }
  }
});

export const {
  setToken,
  resetSessionState,
  logoutUser,
  setSubscriberId,
  setSubscriptionPlanName,
  setSignUpCode,
  setUserNotificationData,
  setIsMediaUploadModalOpen,
  setModuleForUpload,
  setIsFromRecipesPage,
  setIsFromFavoritePage,
  setAdditionalPhotoMeta,
  setFirebaseToken,
  setMemoryFilterOptions,
  setPhotoFilterOptions,
  setRecipeFilterOptions,
  setFolderFilterOptions,
  setMomentFilterOptions,
  setPeopleFilterOptions,
  setTrashFilterOptions
} = sessionStateSlice.actions;
export const getToken = (state: RootState) => state.sessionStateSlice?.token;
export const getRefreshToken = (state: RootState) =>
  state.sessionStateSlice?.refreshToken;
export const getSubscriberId = (state: RootState) =>
  state.sessionStateSlice?.subscriberId;
export const getIsMediaUploadModalOpen = (state: RootState) =>
  state.sessionStateSlice?.isMediaUploadModalOpen;
export const getModuleForUpload = (state: RootState) =>
  state.sessionStateSlice?.moduleForUpload;
export const getUserNotificationData = createSelector(
  [
    (state: RootState) => state.sessionStateSlice?.page,
    (state: RootState) => state.sessionStateSlice?.notificationData
  ],
  (page, notificationData) => ({
    page,
    notificationData
  })
);
export const getIsFromRecipesPage = (state: RootState) =>
  state.sessionStateSlice?.isFromRecipesPage;
export const getIsFromFavoritePage = (state: RootState) =>
  state.sessionStateSlice?.isFromFavoritePage;
export const getAdditionalPhotoMeta = (state: RootState) =>
  state.sessionStateSlice?.additionalPhotoMeta;
export const getFirebaseToken = (state: RootState) =>
  state.sessionStateSlice?.firebaseToken;
export const getMemoryFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.memoryFilterOptions;
export const getPhotoFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.photoFilterOptions;
export const getRecipeFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.recipeFilterOptions;
export const getFolderFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.folderFilterOptions;
export const getMomentFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.momentFilterOptions;
export const getPeopleFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.peopleFilterOptions;
export const getTrashFilterOptions = (state: RootState) =>
  state.sessionStateSlice?.trashFilterOptions;
export const getSubscriptionPlanName = (state: RootState) =>
  state.sessionStateSlice?.subscriptionPlanName;
export const getSignUpCode = (state: RootState) =>
  state.sessionStateSlice?.signUpCode;
